import type { ReactNode } from 'react';

import { cn } from '@platform/ui/lib/utils';

const VALUE_TONES = {
    default: undefined,
    danger: 'text-destructive-strong',
    success: 'text-success-strong',
    warning: 'text-warning-strong',
} as const;

/**
 * One figure on its own tile: an 11px uppercase label over a large tabular
 * number (docs/10 §2 and §4, Card).
 */
export function Stat({
    label,
    value,
    note,
    tone = 'default',
}: {
    label: ReactNode;
    value: ReactNode;
    note?: ReactNode;
    /** Colours the figure when it is itself the alert, a stalled sync say. */
    tone?: keyof typeof VALUE_TONES;
}) {
    return (
        // min-w-0 and the wrap keep a worded value — "Henüz güncellenmedi" — inside
        // a tile two to a phone row; it ran past the tile's edge at 390px.
        <div data-slot="stat" className="flex min-w-0 flex-col rounded-card border border-border bg-card px-5 py-4">
            <span className="text-11 font-medium tracking-label text-muted-foreground uppercase">{label}</span>
            <span className={cn('mt-2 text-28 font-semibold tracking-data wrap-break-word hyphens-auto tabular-nums', VALUE_TONES[tone])}>
                {value}
            </span>
            {note != null && <div className="mt-1 text-13 text-muted-foreground">{note}</div>}
        </div>
    );
}

/** A row of tiles that wraps to fit, every tile at least 160px wide: two across a phone. */
export function StatGrid({ className, children }: { className?: string; children: ReactNode }) {
    return <div className={cn('grid grid-cols-[repeat(auto-fit,minmax(min(10rem,100%),1fr))] gap-4', className)}>{children}</div>;
}
